import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useUserStore } from '@/manage/stores/user'
import { useJobStore, getJobRecentTime, sortJobsByRecentTime } from '@/manage/stores/job'
import { useMembershipPrompt } from '@/manage/composables/upgrade/useMembershipPrompt'
import { userApi } from '@/manage/api/user'
import type { UserJobListPublic } from '@/manage/types/user'
import { isDraftJob } from '@/manage/utils/job'
import { getAgentEntryRoute } from '@/manage/utils/agentEntry'
import { emitJobListSignal } from '@/manage/utils/jobListSignal'
import { jobProfileStorage } from '@/utils/storage/jobProfiles'
import { notifyError, notifySuccess } from '@/utils/error'

type Translate = (key: string, named?: Record<string, unknown>) => string

export const getSidebarJobMenuIndex = (jobId: string) => `/manage/jobs/${jobId}`

export const findSidebarJobById = (jobId: string): UserJobListPublic | undefined =>
  useJobStore().findJobById(jobId)

export const getSidebarJobRecentTime = getJobRecentTime
export const sortSidebarJobsByRecentTime = sortJobsByRecentTime

export const useSidebarJobs = ({ enableJobs, t }: { enableJobs: boolean; t: Translate }) => {
  const route = useRoute()
  const router = useRouter()
  const userStore = useUserStore()
  const jobStore = useJobStore()
  const { showMembershipPrompt } = useMembershipPrompt()
  const sidebarJobs = computed(() => jobStore.jobs)
  const openSidebarJobs = computed(() => jobStore.openJobs)
  const totalJobsCount = computed(() => openSidebarJobs.value.length)
  let sidebarJobsRequest: Promise<void> | null = null

  const fetchSidebarJobs = async () => {
    if (!enableJobs) return
    if (sidebarJobsRequest) return sidebarJobsRequest

    sidebarJobsRequest = (async () => {
      try {
        await jobStore.fetchJobs(true)
      } catch (error) {
        const code = (error as { code?: string } | null)?.code
        const name = (error as { name?: string } | null)?.name
        if (code === 'ERR_CANCELED' || name === 'CanceledError') {
          return
        }
        notifyError({
          scope: 'sidebar fetchSidebarJobs failed',
          error,
          message: t('error.requestFailed'),
        })
      } finally {
        sidebarJobsRequest = null
      }
    })()

    return sidebarJobsRequest
  }

  watch(
    () => [userStore.isAuthenticated, userStore.user?.sub, userStore.isAdminModeActive] as const,
    ([isAuthenticated]) => {
      sidebarJobsRequest = null
      if (!enableJobs) return
      if (!isAuthenticated) {
        jobStore.reset()
        return
      }
      if (jobStore.jobs.length > 0) return
      void fetchSidebarJobs()
    },
    { immediate: true },
  )

  const cacheJobProfile = (job: UserJobListPublic | null) => {
    const jobId = String(job?.id || '').trim()
    const jobName = String(job?.name || '').trim()
    if (jobId && jobName && !job?.read_only) {
      jobProfileStorage.set(jobId, jobName)
    }
    return { jobId, jobName }
  }

  const handleJobClick = (job: UserJobListPublic) => {
    const { jobName } = cacheJobProfile(job)
    if (route.name === 'JobCandidates' && String(route.params.jobId || '') === String(job.id)) {
      void router.replace({
        path: route.path,
        query: {
          ...route.query,
          filter: String(route.query.filter || 'all'),
          profile: jobName || undefined,
          refresh: String(Date.now()),
        },
      })
    }
  }

  const isJobMenuIndex = (index: string) => /^\/manage\/jobs\/[^/?#]+$/.test(String(index || ''))

  const resolveJobFromMenuIndex = (index: string) => {
    const matchedJobId = String(index || '').match(/^\/manage\/jobs\/([^/?#]+)$/)?.[1]
    if (!matchedJobId) return null
    return (
      sidebarJobs.value.find(
        (job) => String(job.id || '').trim() === decodeURIComponent(matchedJobId),
      ) || null
    )
  }

  const handleJobMenuSelect = async (index: string) => {
    if (index === '/manage/jobs?filter=all' && sidebarJobs.value.length === 0) {
      await fetchSidebarJobs()
      if (sidebarJobs.value.length === 0) {
        await router.push(getAgentEntryRoute(null))
        return true
      }
    }
    if (!isJobMenuIndex(index)) return false

    const job = resolveJobFromMenuIndex(index)
    if (job && userStore.isAdminModeActive) {
      await router.push(getAgentEntryRoute(job))
      return true
    }
    const { jobName } = cacheJobProfile(job)
    await router.push({
      path: index,
      query: {
        filter: 'all',
        ...(jobName ? { profile: jobName } : {}),
      },
    })
    return true
  }

  const toggleJobStatus = async (job: UserJobListPublic) => {
    const jobId = String(job.id || '')
    const action = isDraftJob(job) || job.close_at ? 'open' : 'close'
    if (action === 'open' && !userStore.assetIsVip) {
      await showMembershipPrompt({ reason: 'agent-open-job' })
      return
    }

    await userApi.patchUserJobStatus(jobId, action)
    notifySuccess(t('jobs.statusToggleSuccess'))
    const now = new Date().toISOString()
    jobStore.patchJobLocal(jobId, {
      updated_at: now,
      open_at: action === 'open' ? job?.open_at || now : job?.open_at,
      close_at: action === 'close' ? now : null,
    })
    emitJobListSignal({ reason: 'status-changed', jobId })
  }

  const deleteSidebarJob = async (job: UserJobListPublic) => {
    const deletingJobId = String(job.id)
    await userApi.deleteUserJob(deletingJobId)
    notifySuccess(t('jobs.deleteSuccess'))
    jobStore.removeJobLocal(deletingJobId)
    emitJobListSignal({ reason: 'deleted', jobId: deletingJobId })
    return deletingJobId
  }

  return {
    sidebarJobs,
    openSidebarJobs,
    totalJobsCount,
    fetchSidebarJobs,
    handleJobClick,
    handleJobMenuSelect,
    toggleJobStatus,
    deleteSidebarJob,
  }
}
